// client/src/contexts/TenantUnitContext.tsx
// 868mbzt38 — D1 (renter-portal anti-cycle). Mirrors OwnerUnitContext:
// latches the units query and exposes `loadError`/`isRetrying`/`retry`
// so the guard renders a single stable Retry notice instead of
// remounting the portal.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { apiRequest, ApiRequestError } from '@/lib/queryClient';
import { showToast } from '@/lib/toast';
import type { TenantUnitDto } from '@shared/types/tenant/units';

// ── Constants ─────────────────────────────────────────────────────────────────

const SESSION_STORAGE_KEY = 'tenant_active_unit_id';
const USER_SELECTED_KEY = 'tenant_unit_user_selected';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TenantUnitContextValue {
  activeUnit: TenantUnitDto | null;
  allUnits: TenantUnitDto[];
  isLoading: boolean;
  hasUserSelected: boolean;
  switchUnit: (unitId: string) => void;
  loadError: Error | null;
  isRetrying: boolean;
  retry: () => void;
}

// ── Context ───────────────────────────────────────────────────────────────────

const TenantUnitContext = createContext<TenantUnitContextValue>({
  activeUnit: null,
  allUnits: [],
  isLoading: true,
  hasUserSelected: false,
  switchUnit: () => undefined,
  loadError: null,
  isRetrying: false,
  retry: () => undefined,
});

// ── Provider ──────────────────────────────────────────────────────────────────

export function TenantUnitProvider({ children }: { children: ReactNode }) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { data, isLoading, isFetching, error, refetch } = useQuery<TenantUnitDto[]>({
    queryKey: ['tenantUnits'],
    queryFn: () => apiRequest<TenantUnitDto[]>('/api/tenant/units'),
    staleTime: 5 * 60_000,
    // D1: see useOwnedUnits. Once the renter's units query is in error
    // with no data, a new observer attaching after the portal remounted
    // (renter's useTenantAccountsKpis, useTenantOverviewMe, etc.) MUST
    // not trigger a fresh fetch — that's how the renter halves the
    // problem. `retryOnMount: false` skips the on-mount load when the
    // cached entry has data === undefined.
    retryOnMount: false,
  });

  const [latchedError, setLatchedError] = useState<Error | null>(null);
  const [activeUnitId, setActiveUnitId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(SESSION_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [hasUserSelected, setHasUserSelected] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(USER_SELECTED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const units: TenantUnitDto[] = useMemo<TenantUnitDto[]>(
    () => data ?? [],
    [data],
  );

  // 868mbzt38 B4 + B6 — ver OwnerUnitContext: el cerrojo se limpia en
  // `data !== undefined` (no en `units.length > 0`) y un 401 invalida la
  // query de auth para que EX-03 mande al Login si la sesión expiró.
  useLayoutEffect(() => {
    if (data !== undefined) {
      setLatchedError(null);
      return;
    }
    if (error && !isFetching) {
      setLatchedError(error);
    }
  }, [error, data, isFetching]);

  useEffect(() => {
    if (error instanceof ApiRequestError && error.status === 401) {
      void queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
    }
  }, [error, queryClient]);

  const isRetrying = isFetching && latchedError !== null;

  // Synchronous derivation — see OwnerUnitContext. On the very first
  // 868mbzt38 D1 / P1 — la condición es «llegó data», no «hay unidades».
  // Un renter sin unidades (server responde `200 []`) recupera el
  // árbol; un refetch fallido posterior no enciende el aviso otra vez.
  const loadError: Error | null =
    data !== undefined ? null : (error ?? latchedError);

  // Once units load, validate/set the active unit
  useEffect(() => {
    if (isLoading || units.length === 0) {
      return;
    }

    const storedId = activeUnitId;
    const isValid = storedId !== null && units.some((u) => u.id === storedId);

    if (!isValid) {
      const firstId = units[0].id;
      setActiveUnitId(firstId);
      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, firstId);
      } catch {
        // sessionStorage unavailable — ignore
      }
    }
  }, [units, isLoading, activeUnitId]);

  const switchUnit = useCallback(
    (unitId: string) => {
      if (!units.some((u) => u.id === unitId)) {
        showToast.error(
          intl.formatMessage({ id: 'tenant.unitSwitcher.unavailable' }),
          intl.formatMessage({ id: 'tenant.unitSwitcher.unavailableDetail' }),
        );
        queryClient.invalidateQueries({ queryKey: ['tenantUnits'] });
        return;
      }
      setActiveUnitId(unitId);
      setHasUserSelected(true);
      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, unitId);
        sessionStorage.setItem(USER_SELECTED_KEY, '1');
      } catch {
        // sessionStorage unavailable — ignore
      }
      // Invalidate all tenant queries so data refreshes for the new unit
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
    },
    [units, queryClient, intl],
  );

  const retry = useCallback((): void => {
    void refetch();
  }, [refetch]);

  const activeUnit = useMemo<TenantUnitDto | null>(
    () => units.find((u) => u.id === activeUnitId) ?? units[0] ?? null,
    [units, activeUnitId],
  );

  return (
    <TenantUnitContext.Provider
      value={{
        activeUnit,
        allUnits: units,
        isLoading,
        hasUserSelected,
        switchUnit,
        loadError,
        isRetrying,
        retry,
      }}
    >
      {children}
    </TenantUnitContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useTenantUnit(): TenantUnitContextValue {
  return useContext(TenantUnitContext);
}
